import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save } from 'lucide-react';

export function FieldPropertiesDialog({ open, onOpenChange, propsField, propsForm, setPropsForm, allFields, onSave }) {
  if (!propsField) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Field Properties</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/20 border border-border/30">
            <span className="text-[10px] font-mono text-muted-foreground">{propsField}</span>
            {(function() { var f = allFields.find(function(ff) { return ff.name === propsField; }); return f ? <Badge variant="outline" className="text-[8px]">{f.data_type}</Badge> : null; })()}
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground">Display Name</Label>
            <Input value={propsForm.displayName || ''} onChange={function(e) { setPropsForm(function(p) { return Object.assign({}, p, { displayName: e.target.value }); }); }} placeholder="Custom name for this report" className="rounded-lg bg-muted/50 border-border/50 mt-0.5" />
            <p className="text-[8px] text-muted-foreground/70 mt-0.5">Override the default display name for this report only</p>
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground">Format</Label>
            {(function() {
              var f = allFields.find(function(ff) { return ff.name === propsField; });
              var dt = ((f && f.data_type) || '').toLowerCase();
              var isDate = dt.indexOf('date') >= 0 || dt.indexOf('time') >= 0;
              var isNumber = (f && f.semantic_type === 'measure') || ['float', 'int', 'number', 'numeric', 'double', 'decimal'].some(function(t) { return dt.indexOf(t) >= 0; });
              var isString = !isDate && !isNumber;
              var opts;
              if (isDate) {
                opts = [
                  ['_default_', 'Default'],
                  ['MMM YYYY', 'Month Year: Aug 2017'],
                  ['MMM D, YYYY', 'Full: Aug 31, 2017'],
                  ['MM/DD/YYYY', 'US: 08/31/2017'],
                  ['YYYY', 'Year: 2017'],
                ];
              } else if (isString) {
                opts = [
                  ['_default_', 'Default'],
                  ['UPPER', 'UPPERCASE'],
                  ['lower', 'lowercase'],
                  ['Title', 'Title Case'],
                ];
              } else {
                opts = [
                  ['_default_', 'Default'],
                  ['$auto', 'Auto currency: $1.2K / $1.2M / $1.2B'],
                  ['#auto', 'Auto number: 1.2K / 1.2M / 1.2B'],
                  ['#,##0', 'Number: 1,234'],
                  ['#,##0.00', 'Decimal: 1,234.56'],
                  ['$#,##0', 'Currency: $1,234'],
                  ['$#,##0.00', 'Currency: $1,234.56'],
                  ['$#,##0.0K', 'Thousands: $1.2K'],
                  ['$#,##0.0M', 'Millions: $1.2M'],
                  ['$#,##0.0B', 'Billions: $1.2B'],
                  ['#,##0.0K', 'Thousands: 1.2K'],
                  ['#,##0.0M', 'Millions: 1.2M'],
                  ['#0.0%', 'Percent: 65.4%'],
                  ['0', 'Integer: 1234'],
                ];
              }
              return (
                <Select value={propsForm.format || '_default_'} onValueChange={function(v) { setPropsForm(function(p) { return Object.assign({}, p, { format: v === '_default_' ? '' : v }); }); }}>
                  <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-0.5 font-mono text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {opts.map(function(o) { return <SelectItem key={o[0]} value={o[0]}>{o[1]}</SelectItem>; })}
                  </SelectContent>
                </Select>
              );
            })()}
            <p className="text-[8px] text-muted-foreground/70 mt-1">A format chosen here overrides the model's unit and any auto-scaling.</p>
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground">Sort Order</Label>
            <Select value={propsForm.sortOrder || 'default'} onValueChange={function(v) { setPropsForm(function(p) { return Object.assign({}, p, { sortOrder: v }); }); }}>
              <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default</SelectItem>
                <SelectItem value="asc">Ascending (A-Z, 0-9)</SelectItem>
                <SelectItem value="desc">Descending (Z-A, 9-0)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(function() { var f = allFields.find(function(ff) { return ff.name === propsField; }); return f && f.semantic_type === 'measure'; })() && (
            <div>
              <Label className="text-[10px] text-muted-foreground">Aggregation Override</Label>
              <Select value={propsForm.aggregation || '_default_'} onValueChange={function(v) { setPropsForm(function(p) { return Object.assign({}, p, { aggregation: v === '_default_' ? '' : v }); }); }}>
                <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_default_">Default (from model)</SelectItem>
                  <SelectItem value="sum">Sum</SelectItem>
                  <SelectItem value="avg">Average</SelectItem>
                  <SelectItem value="count">Count</SelectItem>
                  <SelectItem value="min">Min</SelectItem>
                  <SelectItem value="max">Max</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-center justify-between">
            <Label className="text-[10px] text-muted-foreground">Show Totals</Label>
            <Switch checked={propsForm.showTotals !== false} onCheckedChange={function(v) { setPropsForm(function(p) { return Object.assign({}, p, { showTotals: v }); }); }} />
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground">Column Width</Label>
            <Select value={propsForm.width || 'auto'} onValueChange={function(v) { setPropsForm(function(p) { return Object.assign({}, p, { width: v }); }); }}>
              <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto</SelectItem>
                <SelectItem value="narrow">Narrow</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="wide">Wide</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={onSave} className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95" data-testid="save-field-props"><Save className="w-4 h-4 mr-2" />Save Properties</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
